/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 데이터포맷관련 클래스를 정의한다.
 *
 * @file /src/utils/Format.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 20.
 */
import { FormatDate } from '@utils/FormatDate';
import { FormatNumber } from '@utils/FormatNumber';

export class Format {
    /**
     * SHA1 해시를 가져온다.
     *
     * @param string - 해시를 만들 문자열
     * @returns 해시
     */
    private static encoder = new TextEncoder();
    static sha1(string: string): string {
        const rotate_left = (n: number, s: number) => {
            return (n << s) | (n >>> (32 - s));
        };

        const cvt_hex = (val: number) => {
            let str = '';
            for (let i = 7; i >= 0; i--) {
                str += ((val >>> (i * 4)) & 0x0f).toString(16);
            }
            return str;
        };

        const W = new Array(80).fill(0);
        let H0 = 0x67452301;
        let H1 = 0xefcdab89;
        let H2 = 0x98badcfe;
        let H3 = 0x10325476;
        let H4 = 0xc3d2e1f0;

        const utf8 = Format.encoder.encode(string);
        const length = utf8.length;

        const word_array: number[] = [];
        for (let i = 0; i < length - 3; i += 4) {
            word_array.push((utf8[i] << 24) | (utf8[i + 1] << 16) | (utf8[i + 2] << 8) | utf8[i + 3]);
        }

        switch (length % 4) {
            case 0:
                word_array.push(0x80000000);
                break;

            case 1:
                word_array.push((utf8[length - 1] << 24) | 0x0800000);
                break;

            case 2:
                word_array.push((utf8[length - 2] << 24) | (utf8[length - 1] << 16) | 0x08000);
                break;

            case 3:
                word_array.push((utf8[length - 3] << 24) | (utf8[length - 2] << 16) | (utf8[length - 1] << 8) | 0x80);
                break;
        }

        while (word_array.length % 16 != 14) {
            word_array.push(0);
        }

        word_array.push(length >>> 29);
        word_array.push((length << 3) & 0x0ffffffff);

        for (let blockstart = 0; blockstart < word_array.length; blockstart += 16) {
            for (let i = 0; i < 16; i++) {
                W[i] = word_array[blockstart + i];
            }
            for (let i = 16; i <= 79; i++) {
                W[i] = rotate_left(W[i - 3] ^ W[i - 8] ^ W[i - 14] ^ W[i - 16], 1);
            }

            let A = H0;
            let B = H1;
            let C = H2;
            let D = H3;
            let E = H4;
            let temp: number;

            for (let i = 0; i <= 19; i++) {
                temp = (rotate_left(A, 5) + ((B & C) | (~B & D)) + E + W[i] + 0x5a827999) & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            for (let i = 20; i <= 39; i++) {
                temp = (rotate_left(A, 5) + (B ^ C ^ D) + E + W[i] + 0x6ed9eba1) & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            for (let i = 40; i <= 59; i++) {
                temp = (rotate_left(A, 5) + ((B & C) | (B & D) | (C & D)) + E + W[i] + 0x8f1bbcdc) & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            for (let i = 60; i <= 79; i++) {
                temp = (rotate_left(A, 5) + (B ^ C ^ D) + E + W[i] + 0xca62c1d6) & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            H0 = (H0 + A) & 0x0ffffffff;
            H1 = (H1 + B) & 0x0ffffffff;
            H2 = (H2 + C) & 0x0ffffffff;
            H3 = (H3 + D) & 0x0ffffffff;
            H4 = (H4 + E) & 0x0ffffffff;
        }

        const hash = cvt_hex(H0) + cvt_hex(H1) + cvt_hex(H2) + cvt_hex(H3) + cvt_hex(H4);
        return hash.toLowerCase();
    }

    /**
     * MD5 해시를 가져온다.
     *
     * @param string - 해시를 만들 문자열
     * @returns 해시
     */
    static md5(string: string): string {
        const safeAdd = (x: number, y: number): number => {
            const lsw = (x & 0xffff) + (y & 0xffff);
            const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
            return (msw << 16) | (lsw & 0xffff);
        };
        const bitRotate = (num: number, cnt: number): number => (num << cnt) | (num >>> (32 - cnt));
        const cmn = (q: number, a: number, b: number, x: number, s: number, t: number): number =>
            safeAdd(bitRotate(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
        const ff = (a: number, b: number, c: number, d: number, x: number, s: number, t: number): number =>
            cmn((b & c) | (~b & d), a, b, x, s, t);
        const gg = (a: number, b: number, c: number, d: number, x: number, s: number, t: number): number =>
            cmn((b & d) | (c & ~d), a, b, x, s, t);
        const hh = (a: number, b: number, c: number, d: number, x: number, s: number, t: number): number =>
            cmn(b ^ c ^ d, a, b, x, s, t);
        const ii = (a: number, b: number, c: number, d: number, x: number, s: number, t: number): number =>
            cmn(c ^ (b | ~d), a, b, x, s, t);

        const str2binl = (str: string): number[] => {
            const bin: number[] = [];
            for (let i = 0; i < str.length * 8; i += 8) {
                bin[i >> 5] = (bin[i >> 5] ?? 0) | ((str.charCodeAt(i / 8) & 0xff) << (i % 32));
            }
            return bin;
        };

        const binl2hex = (binarray: number[]): string => {
            const hexTab = '0123456789abcdef';
            let str = '';
            for (let i = 0; i < binarray.length * 4; i++) {
                str +=
                    hexTab.charAt((binarray[i >> 2] >> ((i % 4) * 8 + 4)) & 0xf) +
                    hexTab.charAt((binarray[i >> 2] >> ((i % 4) * 8)) & 0xf);
            }
            return str;
        };

        const binlMD5 = (x: number[], len: number): number[] => {
            x[len >> 5] |= 0x80 << (len % 32);
            x[(((len + 64) >>> 9) << 4) + 14] = len;

            let a = 1732584193,
                b = -271733879,
                c = -1732584194,
                d = 271733878;

            for (let i = 0; i < x.length; i += 16) {
                const [oa, ob, oc, od] = [a, b, c, d];
                a = ff(a, b, c, d, x[i + 0], 7, -680876936);
                d = ff(d, a, b, c, x[i + 1], 12, -389564586);
                c = ff(c, d, a, b, x[i + 2], 17, 606105819);
                b = ff(b, c, d, a, x[i + 3], 22, -1044525330);
                a = ff(a, b, c, d, x[i + 4], 7, -176418897);
                d = ff(d, a, b, c, x[i + 5], 12, 1200080426);
                c = ff(c, d, a, b, x[i + 6], 17, -1473231341);
                b = ff(b, c, d, a, x[i + 7], 22, -45705983);
                a = ff(a, b, c, d, x[i + 8], 7, 1770035416);
                d = ff(d, a, b, c, x[i + 9], 12, -1958414417);
                c = ff(c, d, a, b, x[i + 10], 17, -42063);
                b = ff(b, c, d, a, x[i + 11], 22, -1990404162);
                a = ff(a, b, c, d, x[i + 12], 7, 1804603682);
                d = ff(d, a, b, c, x[i + 13], 12, -40341101);
                c = ff(c, d, a, b, x[i + 14], 17, -1502002290);
                b = ff(b, c, d, a, x[i + 15], 22, 1236535329);
                a = gg(a, b, c, d, x[i + 1], 5, -165796510);
                d = gg(d, a, b, c, x[i + 6], 9, -1069501632);
                c = gg(c, d, a, b, x[i + 11], 14, 643717713);
                b = gg(b, c, d, a, x[i + 0], 20, -373897302);
                a = gg(a, b, c, d, x[i + 5], 5, -701558691);
                d = gg(d, a, b, c, x[i + 10], 9, 38016083);
                c = gg(c, d, a, b, x[i + 15], 14, -660478335);
                b = gg(b, c, d, a, x[i + 4], 20, -405537848);
                a = gg(a, b, c, d, x[i + 9], 5, 568446438);
                d = gg(d, a, b, c, x[i + 14], 9, -1019803690);
                c = gg(c, d, a, b, x[i + 3], 14, -187363961);
                b = gg(b, c, d, a, x[i + 8], 20, 1163531501);
                a = gg(a, b, c, d, x[i + 13], 5, -1444681467);
                d = gg(d, a, b, c, x[i + 2], 9, -51403784);
                c = gg(c, d, a, b, x[i + 7], 14, 1735328473);
                b = gg(b, c, d, a, x[i + 12], 20, -1926607734);
                a = hh(a, b, c, d, x[i + 5], 4, -378558);
                d = hh(d, a, b, c, x[i + 8], 11, -2022574463);
                c = hh(c, d, a, b, x[i + 11], 16, 1839030562);
                b = hh(b, c, d, a, x[i + 14], 23, -35309556);
                a = hh(a, b, c, d, x[i + 1], 4, -1530992060);
                d = hh(d, a, b, c, x[i + 4], 11, 1272893353);
                c = hh(c, d, a, b, x[i + 7], 16, -155497632);
                b = hh(b, c, d, a, x[i + 10], 23, -1094730640);
                a = hh(a, b, c, d, x[i + 13], 4, 681279174);
                d = hh(d, a, b, c, x[i + 0], 11, -358537222);
                c = hh(c, d, a, b, x[i + 3], 16, -722521979);
                b = hh(b, c, d, a, x[i + 6], 23, 76029189);
                a = hh(a, b, c, d, x[i + 9], 4, -640364487);
                d = hh(d, a, b, c, x[i + 12], 11, -421815835);
                c = hh(c, d, a, b, x[i + 15], 16, 530742520);
                b = hh(b, c, d, a, x[i + 2], 23, -995338651);
                a = ii(a, b, c, d, x[i + 0], 6, -198630844);
                d = ii(d, a, b, c, x[i + 7], 10, 1126891415);
                c = ii(c, d, a, b, x[i + 14], 15, -1416354905);
                b = ii(b, c, d, a, x[i + 5], 21, -57434055);
                a = ii(a, b, c, d, x[i + 12], 6, 1700485571);
                d = ii(d, a, b, c, x[i + 3], 10, -1894986606);
                c = ii(c, d, a, b, x[i + 10], 15, -1051523);
                b = ii(b, c, d, a, x[i + 1], 21, -2054922799);
                a = ii(a, b, c, d, x[i + 8], 6, 1873313359);
                d = ii(d, a, b, c, x[i + 15], 10, -30611744);
                c = ii(c, d, a, b, x[i + 6], 15, -1560198380);
                b = ii(b, c, d, a, x[i + 13], 21, 1309151649);
                a = ii(a, b, c, d, x[i + 4], 6, -145523070);
                d = ii(d, a, b, c, x[i + 11], 10, -1120210379);
                c = ii(c, d, a, b, x[i + 2], 15, 718787259);
                b = ii(b, c, d, a, x[i + 9], 21, -343485551);
                a = safeAdd(a, oa);
                b = safeAdd(b, ob);
                c = safeAdd(c, oc);
                d = safeAdd(d, od);
            }
            return [a, b, c, d];
        };

        return binl2hex(binlMD5(str2binl(string), string.length * 8));
    }

    /**
     * Git 파일해시를 가져온다.
     *
     * @param {File} file - 해시를 가져올 파일객체
     * @returns 파일해시
     */
    static async fileHash(
        file: File,
        callback?: (readed: number, total: number) => void,
        signal?: AbortSignal
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const workerCode = `
            self.importScripts('https://cdn.jsdelivr.net/npm/hash-wasm@4.12.0/dist/sha1.umd.min.js');

            self.onmessage = async (e) => {
                const { file } = e.data;
                const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB

                try {
                    const hasher = await hashwasm.createSHA1();
                    hasher.init();

                    // Git blob header: "blob {size}\\0"
                    const header = new TextEncoder().encode('blob ' + file.size + '\\0');
                    hasher.update(header);

                    let offset = 0;
                    let nextChunkPromise = file.slice(0, Math.min(CHUNK_SIZE, file.size)).arrayBuffer();

                    while (offset < file.size) {
                        const buffer = await nextChunkPromise;
                        offset += buffer.byteLength;

                        // 현재 청크 해싱과 다음 청크 읽기를 병행
                        if (offset < file.size) {
                            nextChunkPromise = file.slice(offset, offset + CHUNK_SIZE).arrayBuffer();
                        }

                        hasher.update(new Uint8Array(buffer));
                        self.postMessage({ type: 'progress', processed: offset });
                    }

                    self.postMessage({ type: 'done', digest: hasher.digest() });
                } catch (err) {
                    self.postMessage({ type: 'error', error: err.message || String(err) });
                }
            };
        `;

            const blob = new Blob([workerCode], { type: 'application/javascript' });
            const blobUrl = URL.createObjectURL(blob);
            const worker = new Worker(blobUrl);
            URL.revokeObjectURL(blobUrl);

            if (signal?.aborted == true) {
                worker.terminate();
                reject(new DOMException('Aborted', 'AbortError'));
                return;
            }

            const onAbort = () => {
                worker.terminate();
                reject(new DOMException('Aborted', 'AbortError'));
            };
            signal?.addEventListener('abort', onAbort, { once: true });

            worker.onerror = (e) => {
                signal?.removeEventListener('abort', onAbort);
                reject(e.message || 'Unknown worker error');
                worker.terminate();
            };

            worker.onmessage = (e) => {
                if (e.data?.type === 'done') {
                    signal?.removeEventListener('abort', onAbort);
                    resolve(e.data.digest);
                    worker.terminate();
                } else if (e.data?.type === 'progress') {
                    callback?.(e.data.processed, file.size);
                } else if (e.data?.type === 'error') {
                    signal?.removeEventListener('abort', onAbort);
                    reject(e.data.error);
                    worker.terminate();
                }
            };

            worker.postMessage({ file });
        });
    }

    /**
     * 숫자 포맷을 지역에 따라 변경한다.
     *
     * @param number - 숫자
     */
    static number(number: number | string): FormatNumber {
        return new FormatNumber(number);
    }

    /**
     * 날짜포맷을 위한 클래스를 가져온다.
     *
     * @param date - 초기날짜 (unixtime or 포맷팅된 날짜)
     * @returns FormatDate
     */
    static date(date: number | string = null): FormatDate {
        return new FormatDate(date);
    }

    /**
     * 부분문자열을 위치에 따라 가져온다.
     *
     * @param string - 문자열
     * @param length - 부분문자열 길이 (양수:앞, 음수:뒤, [앞, 뒤])
     * @returns 부분문자열
     */
    static substring(string: string, length: number | [number, number]): string {
        string = string.trim();

        if (typeof length == 'number') {
            if (length < 0) {
                length = [0, length * -1];
            } else {
                length = [length, 0];
            }
        }

        if (
            Array.isArray(length) == false ||
            length.length != 2 ||
            typeof length[0] != 'number' ||
            typeof length[1] != 'number'
        ) {
            return string;
        }

        const origin = string.length;
        if (origin <= length[0] + length[1]) {
            return string;
        }

        let substring = string.substring(0, length[0]).trim() + '…';
        substring += string.substring(origin - length[1], origin).trim();

        return substring;
    }

    /**
     * 유니코드 문자열을 정규화한다.
     *
     * @param string - 대상문자열
     * @returns NFC 정규화 문자열
     */
    static normalizer(string: string): string {
        return string.normalize('NFC');
    }

    /**
     * byte 단위의 파일크기를 적절한 단위로 변환한다.
     *
     * @param size - 파일크기
     * @param is_KiB - KiB 단위 사용여부
     * @returns 단위를 포함한 파일크기
     */
    static size(size: number | string, is_KiB: boolean = false): string {
        if (typeof size == 'string') {
            size = parseInt(size, 10);
        }

        const depthSize: number = is_KiB === true ? 1024 : 1000;
        if (size / depthSize / depthSize / depthSize > 1) {
            return (size / depthSize / depthSize / depthSize).toFixed(2) + (is_KiB === true ? 'GiB' : 'GB');
        } else if (size / depthSize / depthSize > 1) {
            return (size / depthSize / depthSize).toFixed(2) + (is_KiB === true ? 'MiB' : 'MB');
        } else if (size / depthSize > 1) {
            return (size / depthSize).toFixed(2) + (is_KiB === true ? 'KiB' : 'KB');
        }
        return size + 'B';
    }

    /**
     * 키코드값을 가져온다.
     *
     * @param str - 변환할 텍스트
     * @returns 키코드
     */
    static keycode(str: string): string {
        str = Format.normalizer(str);
        const chos = 'ㄱ,ㄲ,ㄴ,ㄷ,ㄸ,ㄹ,ㅁ,ㅂ,ㅃ,ㅅ,ㅆ,ㅇ,ㅈ,ㅉ,ㅊ,ㅋ,ㅌ,ㅍ,ㅎ'.split(',');
        const jungs = 'ㅏ,ㅐ,ㅑ,ㅒ,ㅓ,ㅔ,ㅕ,ㅖ,ㅗ,ㅘ,ㅙ,ㅚ,ㅛ,ㅜ,ㅝ,ㅞ,ㅟ,ㅠ,ㅡ,ㅢ,ㅣ'.split(',');
        const jongs = ',ㄱ,ㄲ,ㄳ,ㄴ,ㄵ,ㄶ,ㄷ,ㄹ,ㄺ,ㄻ,ㄼ,ㄽ,ㄾ,ㄿ,ㅀ,ㅁ,ㅂ,ㅄ,ㅅ,ㅆ,ㅇ,ㅈ,ㅊ,ㅋ,ㅌ,ㅍ,ㅎ'.split(',');

        let unicode: number[] = [];
        let values: number[] = [];
        let index = 0;

        const encoder = new TextEncoder();
        const decoder = new TextDecoder();
        for (const code of encoder.encode(str)) {
            if (code < 128) {
                unicode.push(code);
            } else {
                if (values.length == 0) {
                    index = code < 224 ? 2 : 3;
                }
                values.push(code);
                if (values.length == index) {
                    const number =
                        index == 3
                            ? (values[0] % 16) * 4096 + (values[1] % 64) * 64 + (values[2] % 64)
                            : (values[0] % 32) * 64 + (values[1] % 64);
                    unicode.push(number);
                    values = [];
                    index = 1;
                }
            }
        }

        let keycode = '';
        for (const code of unicode) {
            if (code >= 44032 && code <= 55203) {
                const temp = code - 44032;
                const cho = Math.floor(temp / 21 / 28);
                const jung = Math.floor((temp % (21 * 28)) / 28);
                const jong = Math.floor(temp % 28);

                keycode += chos[cho] + jungs[jung] + jongs[jong];
            } else {
                if (code < 128) {
                    keycode += decoder.decode(new Uint8Array([code]));
                } else if (code < 2048) {
                    keycode += decoder.decode(new Uint8Array([192 + (code - (code % 64)) / 64, 128 + (code % 64)]));
                } else {
                    keycode += decoder.decode(
                        new Uint8Array([
                            224 + (code - (code % 4096)) / 4096,
                            128 + ((code % 4096) - (code % 64)) / 64,
                            128 + (code % 64),
                        ])
                    );
                }
            }
        }

        return keycode.replace(/ /g, '').toLowerCase();
    }

    /**
     *	두개의 데이터가 동일한지 비교한다.
     *
     *	@param left - 원본객체
     *	@param right - 비교객체
     *	@param is_order - 객체/배열의 순서도 일치할지 여부
     *	@returns 동일한지 여부
     */
    static isEqual(left: any, right: any, is_order: boolean = false): boolean {
        if (left === null || right === null) {
            return (left === right) == true;
        }

        if (typeof left !== typeof right) {
            return false;
        }

        if (Array.isArray(left) == true || Array.isArray(right) == true) {
            if (Array.isArray(left) != Array.isArray(right)) {
                return false;
            }

            if (left.length != right.length) {
                return false;
            }

            if (is_order == true) {
                // 순서 고려
                for (let i = 0; i < left.length; i++) {
                    if (Format.isEqual(left[i], right[i], is_order) == false) {
                        return false;
                    }
                }
            } else {
                // 순서 무시
                const visited = new Array(right.length).fill(false);

                for (const v of left) {
                    let matched = false;

                    for (let i = 0; i < right.length; i++) {
                        if (visited[i] == false && Format.isEqual(v, right[i], is_order) == true) {
                            visited[i] = true;
                            matched = true;
                            break;
                        }
                    }

                    if (matched == false) {
                        return false;
                    }
                }
            }

            return true;
        }

        if (typeof left === 'object') {
            const leftKeys = Object.keys(left);
            const rightKeys = Object.keys(right);

            if (leftKeys.length != rightKeys.length) {
                return false;
            }

            if (is_order == true) {
                // 순서 고려
                for (let i = 0; i < leftKeys.length; i++) {
                    if (leftKeys[i] !== rightKeys[i]) {
                        return false;
                    }

                    const key = leftKeys[i];
                    if (Format.isEqual(left[key], right[key], is_order) == false) {
                        return false;
                    }
                }
            } else {
                // 순서 무시
                for (const key of leftKeys) {
                    if (rightKeys.includes(key) == false) {
                        return false;
                    }

                    if (Format.isEqual(left[key], right[key], is_order) == false) {
                        return false;
                    }
                }
            }

            return true;
        }

        if (typeof left === 'function') {
            return true;
        }

        return (left === right) == true;
    }

    /**
     * 데이터를 정렬한다.
     *
     * @param sorters - 정렬규칙
     * @param left - 왼쪽 데이터
     * @param right - 오른쪽 데이터
     * @returns 정렬결과
     */
    static sorter(
        sorters: { [key: string]: 'ASC' | 'DESC' | string[] },
        left: { [key: string]: any },
        right: { [key: string]: any }
    ): number {
        for (const field in sorters) {
            const direction = sorters[field];

            const aValue = left[field];
            const bValue = right[field];

            if (aValue == null && bValue == null) {
                continue;
            } else if (aValue == null) {
                return direction == 'ASC' ? 1 : -1;
            } else if (bValue == null) {
                return direction == 'ASC' ? -1 : 1;
            }

            if (typeof direction !== 'string') {
                let aIndex = direction.indexOf(aValue.toString());
                aIndex = aIndex == -1 ? direction.length : aIndex;

                let bIndex = direction.indexOf(bValue.toString());
                bIndex = bIndex == -1 ? direction.length : bIndex;

                return aIndex < bIndex ? -1 : 1;
            }

            let compareResult: number = 0;

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else if (aValue instanceof Date && bValue instanceof Date) {
                const aTime = aValue.getTime();
                const bTime = bValue.getTime();
                compareResult = aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
            } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                compareResult = aValue == bValue ? 0 : aValue == true ? 1 : -1;
            } else {
                compareResult = aValue.toString().localeCompare(bValue.toString());
            }

            if (compareResult != 0) {
                return direction == 'ASC' ? compareResult : -compareResult;
            }
        }

        return 0;
    }

    /**
     * 주어진 데이터가 필터조건에 해당하는지 확인한다.
     *
     * @param data - 필터조건에 일치하는지 확인할 데이터
     * @param filters - 필터조건
     * @param mode - 필터모드 (OR, AND)
     * @returns 필터조건만족여부
     */
    static filter(
        data: { [key: string]: any },
        filters: { [field: string]: { value: any; operator: string } },
        filterMode: 'OR' | 'AND' = 'AND'
    ): boolean {
        filters ??= {};
        const operatorHandlers: { [key: string]: (value: any, filterValue: any) => boolean } = {
            '=': (v, fv) => v?.toString() === fv?.toString(),
            '!=': (v, fv) => v?.toString() !== fv?.toString(),
            '>=': (v, fv) => v >= fv,
            '>': (v, fv) => v > fv,
            '<=': (v, fv) => v <= fv,
            '<': (v, fv) => v < fv,
            'in': (v, fv) => Array.isArray(fv) && !Array.isArray(v) && fv.includes(v),
            'not_in': (v, fv) => Array.isArray(fv) && !Array.isArray(v) && !fv.includes(v),
            'like': (v, fv) => v != null && v.indexOf(fv) !== -1,
            'likes': (v, fv) => v != null && fv.split(' ').some((keyword) => v.indexOf(keyword) !== -1),
            'likesall': (v, fv) => v != null && fv.split(' ').every((keyword) => v.indexOf(keyword) !== -1),
            'inset': (v, fv) => Array.isArray(v) && !Array.isArray(fv) && v.includes(fv),
            'likecode': (v, fv) => {
                const keycode = Format.keycode(fv);
                const valuecode = v == null ? null : Format.keycode(v);
                return valuecode != null && valuecode.indexOf(keycode) !== -1;
            },
            'range': (v, fv) => {
                if (fv == null || typeof fv !== 'object') {
                    return false;
                }
                const { start, end } = fv;
                let startPassed = true;
                let endPassed = true;
                if (start != null) {
                    if (start.operator === '>=') startPassed = v >= start.value;
                    else if (start.operator === '>') startPassed = v > start.value;
                }
                if (end != null) {
                    if (end.operator === '<=') endPassed = v <= end.value;
                    else if (end.operator === '<') endPassed = v < end.value;
                }
                return startPassed == true && endPassed == true;
            },
            'date': (v, fv) => {
                if (v == null || fv == null) {
                    return false;
                }
                const { operator: dateOp, format, value: dateValue, range } = fv;

                let date: Date;
                if (format === 'timestamp') {
                    date = new Date((typeof v === 'string' ? parseInt(v, 10) : v) * 1000);
                } else {
                    date = new Date(typeof v === 'string' ? v.replace(' ', 'T') : v);
                }

                if (isNaN(date.getTime()) == true) {
                    return false;
                }

                const now = new Date();
                const pad = (n: number) => String(n).padStart(2, '0');
                const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                const dateStr = toDateStr(date);

                if (dateOp === 'today') {
                    return dateStr === toDateStr(now);
                }
                if (dateOp === 'this_week') {
                    const dayOfWeek = now.getDay();
                    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                    const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
                    const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset + 6);
                    return dateStr >= toDateStr(weekStart) && dateStr <= toDateStr(weekEnd);
                }
                if (dateOp === 'this_month') {
                    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
                }
                if (dateOp === 'this_year') {
                    return date.getFullYear() === now.getFullYear();
                }
                if (dateOp === '=') {
                    return dateStr === dateValue;
                }
                if (dateOp === '>=') {
                    return dateStr >= dateValue;
                }
                if (dateOp === '<=') {
                    return dateStr <= dateValue;
                }
                if (dateOp === 'range') {
                    if (Array.isArray(range) == false || range.length < 2) {
                        return false;
                    }
                    return dateStr >= range[0] && dateStr <= range[1];
                }
                return false;
            },
        };

        for (const field in filters) {
            const { value: filterValue, operator } = filters[field];
            const value = data[field] ?? null;

            const passed = operatorHandlers[operator] != null ? operatorHandlers[operator](value, filterValue) : false;

            if (filterMode === 'AND' && passed == false) {
                return false;
            } else if (filterMode === 'OR' && passed == true) {
                return true;
            }
        }

        return filterMode === 'AND';
    }

    /**
     * 좌우 특정 문자열을 제거한다.
     *
     * @param str - 원본문자열
     * @param char - 제거할 문자열
     * @returns 제거된 문자열
     */
    static trim(str: string, char: string = ' '): string {
        return Format.rtrim(Format.ltrim(str, char), char);
    }

    /**
     * 좌측 특정 문자열을 제거한다.
     *
     * @param str - 원본문자열
     * @param char - 제거할 문자열
     * @returns 제거된 문자열
     */
    static ltrim(str: string, char: string = ' '): string {
        return str.replace(new RegExp(`^(${char})+`), '');
    }

    /**
     * 우측 특정 문자열을 제거한다.
     *
     * @param str - 원본문자열
     * @param char - 제거할 문자열
     * @returns 제거된 문자열
     */
    static rtrim(str: string, char: string = ' '): string {
        return str.replace(new RegExp(`(${char})+$`), '');
    }
}
